/**
 * net/visits.js — walking into someone else's personal space (GDD 9.2, 10.5).
 *
 * Three messages and nothing else: you send `visit-req`, the owner answers with
 * `visit-data` (a serialised snapshot of their space), and you may send back a
 * `reaction` for their guestbook. The visitor's copy is read-only by
 * construction — a snapshot is data, there is no protocol for changing it, and
 * nothing here writes to the owner's save.
 *
 * The personal-space module is owned by another agent and is still being built,
 * so every call into it is feature-detected and the module itself is loaded
 * with a guarded dynamic import. If `space.serialize()` does not exist yet a
 * local fallback assembles an equivalent snapshot straight from the save, which
 * means visits work end to end today and get better for free the moment the
 * real implementation lands.
 *
 * @module net/visits
 */

import { EVENTS, NET, VISIT_REACTIONS } from '../constants.js';
import { bus } from '../core/events.js';
import { save } from '../core/save.js';
import {
  packReaction, packVisitData, packVisitReq, sanitizeName, sanitizeReaction, unpack,
} from './protocol.js';

/** Resolved space module, or null if it could not be loaded. */
let spaceModule = null;
let spaceLoadAttempted = false;

/**
 * Load `space/` without letting a half-written module take the game down with
 * it. Eight agents share this checkout; a static import would couple this
 * module's health to another agent's current save state.
 * @returns {Promise<object|null>}
 */
async function getSpace() {
  if (spaceLoadAttempted) return spaceModule;
  spaceLoadAttempted = true;
  try {
    spaceModule = await import('../space/index.js');
  } catch (err) {
    console.warn('[net] personal space module unavailable:', err?.message);
    spaceModule = null;
  }
  return spaceModule;
}

/**
 * Snapshot the local player's space for a visitor.
 *
 * Prefers `space.serialize()`. The fallback reads the same save keys the space
 * module is built on, so the two produce the same shape of thing.
 *
 * @param {string} ownerName
 * @returns {Promise<object>}
 */
export async function serializeLocalSpace(ownerName) {
  const space = await getSpace();

  if (typeof space?.serialize === 'function') {
    try {
      const data = await space.serialize();
      if (data && typeof data === 'object') {
        return { ownerName, ...data };
      }
    } catch (err) {
      console.warn('[net] space.serialize() failed, using fallback:', err?.message);
    }
  }

  const d = save.data;
  return {
    version: 1,
    fallback: true,
    ownerName,
    shelfSlots: d.space?.shelfSlots ?? 12,
    tankCapacity: d.space?.tankCapacity ?? 5,
    theme: d.space?.theme ?? null,
    decor: (d.space?.decor || []).slice(0, 48),
    plush: (d.prizes?.plush || []).slice(0, 64).map(trimPrize),
    fish: (d.prizes?.fish || []).slice(0, 64).map(trimPrize),
    stats: { totalPlays: d.stats?.totalPlays ?? 0, stars: d.wallet?.stars ?? 0 },
  };
}

/** @param {object} p @returns {object} Only the fields a visitor needs to see. */
function trimPrize(p) {
  return {
    id: p.id, kind: p.kind, variant: p.variant, name: p.name, day: p.day,
  };
}

/**
 * Visit request/response plus the guestbook, over the peer transport.
 *
 * Held as a class with an injected `send` so the whole flow can be exercised
 * against a loopback in tests — which is how the visit spec runs without a
 * second browser.
 */
class Visits {
  constructor() {
    /** @type {(peerId:string, message:object)=>void} */
    this.send = () => {};
    /** Local display name, set by presence on connect. */
    this.localName = 'Goose';
    /** @type {{ peerId:string, data:object }|null} The space being viewed. */
    this.viewing = null;
    /** Reactions received while the space module had no guestbook. */
    this.pendingReactions = [];
    this.served = 0;
    this.rendered = 0;
  }

  /**
   * Ask a peer to show you their space.
   * @param {string} peerId
   * @returns {boolean}
   */
  request(peerId) {
    if (!peerId) return false;
    this.send(peerId, packVisitReq());
    bus.emit(EVENTS.UI_TOAST, { text: 'Knocking…', kind: 'good', duration: 1600 });
    return true;
  }

  /**
   * Somebody knocked. Answer with a snapshot — automatically, because a visit
   * prompt that needs accepting is a visit that never happens.
   * @param {string} peerId
   */
  async onRequest(peerId) {
    const data = await serializeLocalSpace(this.localName);
    this.served++;
    this.send(peerId, packVisitData({ data }));
  }

  /**
   * A snapshot arrived. Hand it to the space module to render read-only.
   * @param {string} peerId
   * @param {object} data Already size-capped and name-sanitised by the protocol.
   * @returns {Promise<boolean>} Whether the space module rendered it.
   */
  async onData(peerId, data) {
    this.viewing = { peerId, data };
    const owner = sanitizeName(data.ownerName, 'a goose');
    const space = await getSpace();

    let rendered = false;
    if (typeof space?.renderVisit === 'function') {
      try {
        await space.renderVisit(data);
        rendered = true;
        this.rendered++;
      } catch (err) {
        console.warn('[net] space.renderVisit() failed:', err?.message);
      }
    }

    bus.emit(EVENTS.UI_TOAST, {
      text: rendered ? `Visiting ${owner}'s space` : `${owner}'s space is not ready to show`,
      kind: rendered ? 'prize' : 'bad',
      duration: 2600,
    });
    return rendered;
  }

  /**
   * Leave a reaction in the space you are visiting.
   * @param {'honk'|'applause'|'wow'} reaction
   * @param {string} [peerId] Defaults to the space currently being viewed.
   * @returns {boolean}
   */
  react(reaction, peerId = this.viewing?.peerId) {
    const clean = sanitizeReaction(reaction);
    if (!clean || !peerId) return false;
    this.send(peerId, packReaction({ reaction: clean, fromName: this.localName }));
    bus.emit(EVENTS.AUDIO_PLAY, { name: clean === 'honk' ? 'honk' : 'cheer', volume: 0.5 });
    return true;
  }

  /**
   * A visitor left a reaction in YOUR guestbook (GDD 9.4).
   * @param {string} reaction
   * @param {string} fromName
   * @returns {Promise<boolean>} Whether the space module accepted it.
   */
  async onReaction(reaction, fromName) {
    const clean = sanitizeReaction(reaction);
    if (!clean) return false;
    const who = sanitizeName(fromName);
    const space = await getSpace();

    let accepted = false;
    if (typeof space?.guestbook?.add === 'function') {
      try {
        space.guestbook.add(clean, who);
        accepted = true;
      } catch (err) {
        console.warn('[net] guestbook.add() failed:', err?.message);
      }
    }
    // Held so the space module can drain them once it exists — a reaction that
    // arrives before the guestbook does should not just evaporate.
    if (!accepted) {
      this.pendingReactions.push({ reaction: clean, fromName: who, t: Date.now() });
      if (this.pendingReactions.length > 32) this.pendingReactions.shift();
    }

    bus.emit(EVENTS.UI_TOAST, {
      text: `${who} left a ${REACTION_LABEL[clean]}`,
      kind: 'prize',
      duration: 2800,
    });
    return accepted;
  }

  /** Leave the space you are viewing. */
  leave() {
    this.viewing = null;
  }

  /** @returns {object} Test-facing snapshot. */
  state() {
    return {
      viewing: this.viewing ? this.viewing.peerId : null,
      ownerName: this.viewing ? this.viewing.data.ownerName : null,
      served: this.served,
      rendered: this.rendered,
      pendingReactions: this.pendingReactions.slice(),
      reactions: [...VISIT_REACTIONS],
    };
  }
}

const REACTION_LABEL = Object.freeze({
  honk: 'HONK', applause: 'round of applause', wow: 'wow',
});

/** The one visit controller. */
export const visits = new Visits();

/**
 * Run a whole visit end to end against a loopback, for `__cc.net.visitTest()`.
 *
 * Serialises the local space, sends it through pack → unpack exactly as the
 * wire would, renders it, then round-trips a guestbook reaction back. No peer,
 * no broker, no second browser — which is the only way this is testable in CI.
 *
 * @param {{ fromName?:string, reaction?:string }} [opts]
 * @returns {Promise<object>}
 */
export async function visitTest(opts = {}) {
  const fromName = opts.fromName || 'Gustav';
  const reaction = opts.reaction || 'honk';
  const previousSend = visits.send;

  /** @type {object[]} */
  const wire = [];
  visits.send = (_peerId, message) => wire.push(message);

  try {
    await visits.onRequest('loopback');
    const sent = wire.find((m) => m.t === 'visit-data');
    // The unpack step is the point: the visitor never sees raw sender data.
    const received = sent ? unpack(sent) : null;
    const rendered = received ? await visits.onData('loopback', received.data) : false;

    const reactionMsg = packReaction({ reaction, fromName });
    const reactionIn = unpack(reactionMsg);
    const accepted = reactionIn
      ? await visits.onReaction(reactionIn.reaction, reactionIn.fromName)
      : false;

    return {
      served: Boolean(sent),
      packedType: sent?.t ?? null,
      unpacked: Boolean(received),
      ownerName: received?.data?.ownerName ?? null,
      usedFallback: Boolean(received?.data?.fallback),
      prizeCount: (received?.data?.plush?.length ?? 0) + (received?.data?.fish?.length ?? 0),
      rendered,
      reactionAccepted: accepted,
      reactionQueued: visits.pendingReactions.length,
      reactionFrom: visits.pendingReactions.at(-1)?.fromName ?? null,
      maxVisitBytes: NET.maxVisitBytes,
    };
  } finally {
    visits.send = previousSend;
  }
}
